import { morph } from '@theme/morph';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { prefersReducedMotion } from '@theme/utilities';

class CartRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-ids'];
  #drawer;
  #section;
  #track;
  #controller;
  #generation = 0;
  #loaded;
  #requested;
  #heldAdds = new Map();

  connectedCallback() {
    this.#section = this.querySelector('section');
    this.#track = this.querySelector('[data-track]');
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#sync);
    this.#track?.addEventListener('keydown', this.#onKeyDown);
    this.#sync();
  }

  disconnectedCallback() {
    this.#generation++;
    this.#controller?.abort();
    this.#requested = undefined;
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#sync);
    this.#track?.removeEventListener('keydown', this.#onKeyDown);
    this.#heldAdds.clear();
  }

  attributeChangedCallback() {
    if (this.isConnected && this.#section) this.#sync();
  }

  #ids() {
    try { return [...new Set(JSON.parse(this.dataset.productIds || '[]').map(String))]; }
    catch { return []; }
  }

  #sync = () => {
    const ids = this.#ids();
    const key = ids.join(',');
    const excluded = new Set(ids);
    for (const card of [...(this.#track?.children || [])]) {
      if (!excluded.has(card.dataset.cartRecommendationProductId) || [...this.#heldAdds.values()].includes(card)) continue;
      if (card.contains(document.activeElement)) {
        (card.nextElementSibling?.querySelector('button, a[href]') || this.querySelector('h3'))?.focus({ preventScroll: true });
      }
      card.remove();
    }

    if (!ids.length) {
      this.#generation++;
      this.#controller?.abort();
      this.#requested = undefined;
      this.#loaded = undefined;
      this.#section.hidden = true;
      this.#section.removeAttribute('aria-busy');
      return;
    }
    if (this.#requested && this.#requested !== key) {
      this.#generation++;
      this.#controller?.abort();
      this.#requested = undefined;
    }
    if (this.#heldAdds.size || (this.#drawer && !this.#drawer.querySelector('dialog')?.open)) return;
    if (this.#loaded === key || this.#requested === key) return;
    this.#load(ids, key);
  };

  async #load(ids, key) {
    this.#controller?.abort();
    const controller = this.#controller = new AbortController();
    const generation = ++this.#generation;
    this.#requested = key;
    this.#section.hidden = false;
    this.#section.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-status]');
    status.hidden = false;
    try {
      const responses = await Promise.allSettled(ids.slice(0, 3).map(async id => {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('product_id', id);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return [...html.querySelectorAll('[data-cart-recommendation-product-id]')];
      }));
      if (generation !== this.#generation || !this.isConnected || this.#heldAdds.size) return;
      const excluded = new Set(this.#ids());
      const lists = responses.filter(result => result.status === 'fulfilled').map(result =>
        result.value.filter(card => !excluded.has(card.dataset.cartRecommendationProductId))
      );
      if (!lists.length) throw new Error('Recommendations unavailable');
      const selected = [], seen = new Set(this.#ids());
      for (let rank = 0; rank < 10 && selected.length < 8; rank++) {
        for (const list of lists) {
          const card = list[rank];
          const id = card?.dataset.cartRecommendationProductId;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          selected.push(card);
          if (selected.length === 8) break;
        }
      }
      const focused = this.#track.contains(document.activeElement);
      const next = document.createElement('ul');
      const available = new Map(lists.flat().map(card => [card.dataset.cartRecommendationProductId, card]));
      const retained = [...this.#track.children].map(card => available.get(card.dataset.cartRecommendationProductId)).filter(Boolean);
      const merged = [...new Map([...retained, ...selected].map(card => [card.dataset.cartRecommendationProductId, card])).values()].slice(0, 8);
      next.append(...merged);
      morph(this.#track, next);
      this.#loaded = key;
      this.#section.hidden = merged.length === 0;
      if (focused && !this.#track.contains(document.activeElement)) this.querySelector('h3')?.focus({ preventScroll: true });

    } catch (error) {
      if (generation === this.#generation && !controller.signal.aborted) this.#section.hidden = true;
    } finally {
      if (generation === this.#generation) {
        this.#requested = undefined;
        this.#section.removeAttribute('aria-busy');
        status.hidden = true;
      }
    }
  }

  holdAdd(promise, source) {
    const card = source?.closest('[data-cart-recommendation-product-id]');
    if (card && this.contains(card)) this.#heldAdds.set(promise, card);
  }

  releaseAdd(promise) {
    const card = this.#heldAdds.get(promise);
    card?.removeAttribute('data-confirmed');
    this.#heldAdds.delete(promise);
    if (this.isConnected) this.#sync();
  }

  async finishAdd(promise) {
    const card = this.#heldAdds.get(promise);
    if (!card) return;
    card.setAttribute('data-confirmed', '');
    await new Promise(resolve => setTimeout(resolve, 300));
    if (!this.isConnected || this.#heldAdds.get(promise) !== card) return;
    const moving = !prefersReducedMotion() && this.#drawer?.querySelector('dialog')?.open;
    if (moving) {
      const direction = getComputedStyle(this).direction === 'rtl' ? 1 : -1;
      await card.animate([{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: `translateX(${direction * 24}px)` }], { duration: 180, easing: 'ease-out', fill: 'forwards' }).finished.catch(() => {});
    }
    if (!this.isConnected || this.#heldAdds.get(promise) !== card) return;
    const positions = new Map([...this.#track.children].map(node => [node, node.getBoundingClientRect().left]));
    const focused = card.contains(document.activeElement);
    card.remove();
    this.#track.scrollLeft = 0;
    if (focused) this.querySelector('h3')?.focus({ preventScroll: true });
    if (moving) for (const node of this.#track.children) {
      const delta = positions.get(node) - node.getBoundingClientRect().left;
      if (delta) node.animate([{ transform: `translateX(${delta}px)` }, { transform: 'translateX(0)' }], { duration: 180, easing: 'ease-out' });
    }
    this.releaseAdd(promise);
  }

  #onKeyDown = event => {
    if (event.target !== this.#track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    this.#track.scrollBy({ left: this.#track.clientWidth * 0.85 * (event.key === 'ArrowRight' ? 1 : -1), behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  };
}

if (!customElements.get('cart-recommendations')) customElements.define('cart-recommendations', CartRecommendations);
